// What a push is doing, as events rather than a sentence.
//
// The panel inside Live wants one line of prose and a percentage; the web
// export screen wants a stage, a count, and a row per sample that turns green
// as it lands. Both come from the same push, so the push emits structured
// events and each host renders what it needs — `textOf` rebuilds the prose,
// so the panel's copy is unchanged.
//
// Host-agnostic, like the rest of the push core: no Node, no Live, no DOM.

/**
 * Where the work is up to. Counts ride on the event, not the name.
 *
 * Both directions share this vocabulary: pushing a seed INTO Audiotool
 * (project → fetching → uploading → writing) and capturing a project back OUT
 * of it (reading → rendering → downloading → cutting → mixing → encoding →
 * publishing).
 * One list means one progress screen serves both.
 */
export type PushStage =
  | "project"
  | "fetching-audio"
  | "fetching-stems"
  | "uploading"
  | "writing"
  | "reading"
  | "rendering"
  | "downloading"
  | "cutting"
  | "mixing"
  | "encoding"
  | "publishing"
  | "done";

/**
 * What has happened to one sample. A sample the library already holds is
 * `reused` — it never leaves the browser — and `missing` is a clip whose audio
 * couldn't be fetched at all, which is reported and skipped, never fatal.
 */
export type SampleState =
  | "downloading"
  | "downloaded"
  | "missing"
  | "uploading"
  | "uploaded"
  | "reused"
  | "failed";

export type PushEvent =
  | { kind: "stage"; stage: PushStage; done?: number; total?: number }
  /**
   * `id` is whatever identifies the sample at this point in the push: a seed's
   * audioKey (or a stem's name) while it is being fetched, and its content tag
   * once uploading. The `tag` on a `downloaded` event is what joins the two,
   * so a host that keeps one row per sample can follow it across the seam.
   */
  | { kind: "sample"; id: string; displayName: string; state: SampleState; bytes?: number; tag?: string };

export type PushReporter = (event: PushEvent) => void;

/** Text and percentage for an event, or null for events with no prose. */
export function textOf(event: PushEvent): { text: string; percent: number } | null {
  if (event.kind !== "stage") return null;
  const done = event.done ?? 0;
  const total = event.total ?? 0;
  const share = (of: number): number => Math.round((done / Math.max(1, total)) * of);
  switch (event.stage) {
    case "project":
      return { text: "Creating the Audiotool project…", percent: 55 };
    case "fetching-audio":
      return { text: `Fetching clip audio (${done}/${total})…`, percent: share(50) };
    case "fetching-stems":
      return { text: `Fetching stems (${done}/${total})…`, percent: share(50) };
    case "uploading":
      // Before the first sample lands this is the announcement, not a counter.
      return done === 0
        ? { text: total > 0 ? `Uploading ${total} sample(s)…` : "Nothing new to upload", percent: 60 }
        : { text: `Uploading samples (${done}/${total})…`, percent: 60 + share(30) };
    case "writing":
      return { text: "Writing to Audiotool…", percent: 92 };
    case "reading":
      return { text: "Reading the Audiotool project…", percent: 5 };
    case "rendering":
      return { text: total > 0 ? `Audiotool is rendering (${done}/${total})…` : "Asking Audiotool to render…", percent: 5 + share(25) };
    case "downloading":
      return { text: `Downloading samples (${done}/${total})…`, percent: 30 + share(15) };
    case "cutting":
      return { text: "Cutting the clips…", percent: 50 };
    case "mixing":
      return { text: "Rendering the master mix…", percent: 60 };
    case "encoding":
      return { text: "Encoding previews…", percent: 70 };
    case "publishing":
      return { text: total > 0 ? `Uploading to Seedbed (${done}/${total})…` : "Uploading to Seedbed…", percent: 75 + share(20) };
    case "done":
      return { text: "Done.", percent: 100 };
  }
}

/** A reporter that speaks to a prose-and-percentage listener. */
export function reportToUpdate(update: (text: string, percent?: number) => Promise<void>): PushReporter {
  return (event) => {
    const line = textOf(event);
    if (line) void update(line.text, line.percent);
  };
}

/** Fan out to every reporter given; absent ones are ignored. */
export function reportToAll(...reporters: ReadonlyArray<PushReporter | undefined>): PushReporter {
  const live = reporters.filter((r): r is PushReporter => !!r);
  return (event) => {
    for (const r of live) r(event);
  };
}
